/**
 * 解析結果表示パネル
 *
 * 音声解析の結果を統合的に表示するメインコンポーネント
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Mic as MicIcon,
  TextFields as TextFieldsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { bg, border, statusBg } from '../../../../utils/darkModeAdapter';

import ScoreRadarChart from './ScoreRadarChart';
import DeliveryMetrics from './DeliveryMetrics';
import ContentMetrics from './ContentMetrics';
import TranscriptionView from './TranscriptionView';

/**
 * 解析結果表示パネル
 */
export default function AnalysisResultsPanel({
  analysisResults,
  isLoading = false,
  error = null
}) {
  const theme = useTheme();
  const [showImprovements, setShowImprovements] = useState(true);
  const [expandedPanel, setExpandedPanel] = useState('delivery'); // 'delivery' | 'content' | 'transcription' | null

  // アコーディオンの展開制御
  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          解析結果を読み込み中...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analysisResults) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          解析結果がありません
        </Typography>
      </Paper>
    );
  }

  const { scores, transcription, acoustic_analysis, language_analysis } = analysisResults;

  // スコアの色を決定
  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* ヘッダー：総合スコア */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            音声解析結果
          </Typography>
          {scores && (
            <Chip
              label={`総合スコア: ${scores.overall_audio_score}点`}
              color={getScoreColor(scores.overall_audio_score)}
              size="large"
              icon={scores.overall_audio_score >= 70 ? <CheckCircleIcon /> : <WarningIcon />}
            />
          )}
        </Box>

        {/* スコア説明 */}
        {scores?.score_explanation && (
          <Alert
            severity={scores.overall_audio_score >= 70 ? 'success' : 'info'}
            sx={{ mb: 2 }}
          >
            {scores.score_explanation}
          </Alert>
        )}

        {/* 改善提案 */}
        {scores?.improvement_suggestions && scores.improvement_suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1
              }}
              onClick={() => setShowImprovements(!showImprovements)}
            >
              <LightbulbIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2">
                改善ポイント ({scores.improvement_suggestions.length}件)
              </Typography>
              <IconButton size="small">
                {showImprovements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={showImprovements}>
              <Box sx={{ pl: 4 }}>
                {scores.improvement_suggestions.map((suggestion, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ mb: 0.5, color: 'text.secondary' }}
                  >
                    {index + 1}. {suggestion}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* レーダーチャート（常に表示） */}
      {scores && (
        <Box sx={{ mb: 3 }}>
          <ScoreRadarChart scores={scores} />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* アコーディオン形式の詳細セクション */}
      <Box>
        {/* 話し方の詳細 */}
        <Accordion
          expanded={expandedPanel === 'delivery'}
          onChange={handlePanelChange('delivery')}
          elevation={0}
          sx={{
            border: `1px solid ${border(theme).light}`,
            borderRadius: 2,
            mb: 1,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0, mb: 1 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: expandedPanel === 'delivery' ? statusBg(theme).info : bg(theme).subtle,
              borderRadius: expandedPanel === 'delivery' ? '8px 8px 0 0' : 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicIcon sx={{ color: '#249BE0' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                話し方の詳細
              </Typography>
              {scores?.delivery_composite_score && (
                <Chip
                  label={`${scores.delivery_composite_score}点`}
                  size="small"
                  color={getScoreColor(scores.delivery_composite_score)}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2 }}>
            <DeliveryMetrics
              deliveryScores={scores?.delivery_scores}
              compositeScore={scores?.delivery_composite_score}
              acousticAnalysis={acoustic_analysis}
            />
          </AccordionDetails>
        </Accordion>

        {/* 内容の詳細 */}
        <Accordion
          expanded={expandedPanel === 'content'}
          onChange={handlePanelChange('content')}
          elevation={0}
          sx={{
            border: `1px solid ${border(theme).light}`,
            borderRadius: 2,
            mb: 1,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0, mb: 1 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: expandedPanel === 'content' ? statusBg(theme).success : bg(theme).subtle,
              borderRadius: expandedPanel === 'content' ? '8px 8px 0 0' : 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: '#22c55e' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                内容の詳細
              </Typography>
              {scores?.content_composite_score && (
                <Chip
                  label={`${scores.content_composite_score}点`}
                  size="small"
                  color={getScoreColor(scores.content_composite_score)}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2 }}>
            <ContentMetrics
              contentScores={scores?.content_scores}
              compositeScore={scores?.content_composite_score}
              languageAnalysis={language_analysis}
            />
          </AccordionDetails>
        </Accordion>

        {/* 書き起こし */}
        <Accordion
          expanded={expandedPanel === 'transcription'}
          onChange={handlePanelChange('transcription')}
          elevation={0}
          sx={{
            border: `1px solid ${border(theme).light}`,
            borderRadius: 2,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: expandedPanel === 'transcription' ? statusBg(theme).warning : bg(theme).subtle,
              borderRadius: expandedPanel === 'transcription' ? '8px 8px 0 0' : 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextFieldsIcon sx={{ color: '#f59e0b' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                書き起こし
              </Typography>
              {transcription?.text && (
                <Chip
                  label={`${transcription.text.length > 100 ? (transcription.text.length / 100).toFixed(0) + '00+文字' : transcription.text.length + '文字'}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2 }}>
            <TranscriptionView
              transcription={transcription}
              highlights={language_analysis?.highlight_segments}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );
}
